import { ListOrderedIcon, SearchXIcon } from "lucide-react"
import { useTranslation } from "react-i18next"

import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  useOrdersByFilter,
  useOrdersSearchParams,
} from "@/hooks"

import { AppPagination } from "../commons/app-pagination"
import { AppSkeletonTable } from "../commons/app-skeleton-table"
import { OrdersTableRow } from "./table-row"

export const OrdersTable = () => {
  const { t } = useTranslation()
  const { params } = useOrdersSearchParams()
  const { data, isFetching } = useOrdersByFilter(
    params,
    Boolean(params.refetch)
  )
  const pagination = data?.pagination
  const orders = data?.orders || []
  const hasOrders = orders.length > 0
  const paginationData = pagination
    ? {
        currentPage: pagination.currentPage,
        total: pagination.totalResults,
        totalPages: pagination.totalPages,
      }
    : {
        currentPage: params.page,
        total: data?.total || 0,
        totalPages: Math.ceil(
          (data?.total || 0) / params.pageSize
        ),
      }

  if (isFetching) return <AppSkeletonTable columns={9} />

  if (!params.refetch) {
    return (
      <div className="mt-4 flex flex-1 flex-col items-center justify-center gap-4 text-muted-foreground">
        <ListOrderedIcon size={48} strokeWidth={1.5} />
        <p className="text-center">
          {t("pages.orders.searchToView")}
        </p>
      </div>
    )
  }

  if (!hasOrders) {
    return (
      <div className="mt-4 flex flex-1 flex-col items-center justify-center gap-4 text-muted-foreground">
        <SearchXIcon size={48} strokeWidth={1.5} />
        <div className="text-center">
          <p className="font-medium">
            {t("pages.orders.noOrdersFound")}
          </p>
          <p className="text-sm">
            {t("pages.orders.tryDifferentFilters")}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="relative overflow-y-auto overflow-x-hidden rounded-md border border-border">
        <Table
          className="relative h-10 w-full overflow-clip rounded-md border-border"
          divClassname="max-h-[68vh] overflow-y-scroll scrollbar"
        >
          <TableHeader className="sticky top-0 z-20 h-10 w-full bg-background outline outline-1 outline-border">
            <TableRow>
              <TableHead className="w-[140px]">
                {t("orders.table.identification")}
              </TableHead>
              <TableHead className="hidden w-[280px] md:table-cell">
                {t("orders.table.orderId")}
              </TableHead>
              <TableHead className="hidden w-[240px] lg:table-cell">
                {t("orders.table.customer")}
              </TableHead>
              <TableHead className="hidden w-[140px] lg:table-cell">
                {t("orders.table.salesChannel")}
              </TableHead>
              <TableHead className="hidden w-[150px] lg:table-cell">
                {t("orders.table.paymentMethod")}
              </TableHead>
              <TableHead className="hidden w-[100px] text-end lg:table-cell">
                {t("orders.table.discount")}
              </TableHead>
              <TableHead className="hidden w-[100px] text-end sm:table-cell">
                {t("orders.table.amount")}
              </TableHead>
              <TableHead className="hidden w-[140px] lg:table-cell">
                {t("orders.table.dates.title")}
              </TableHead>
              <TableHead className="hidden w-[120px] text-center md:table-cell">
                {t("orders.table.actions")}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {orders.map((order) => (
              <OrdersTableRow
                key={order.id || order.uuid}
                order={order}
              />
            ))}
          </TableBody>
        </Table>
      </div>
      <AppPagination pagination={paginationData} />
    </div>
  )
}
